import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface NoteDialogData {
    mode: 'add' | 'edit' | 'view';
    caseTitle?: string;
    existingContent?: string; // 'edit' and 'view' modes
}

@Component({
    selector: 'app-note-dialog',
    templateUrl: './note-dialog.component.html',
    styleUrls: ['./note-dialog.component.scss'],
    standalone: false
})
export class NoteDialogComponent {

    mode: 'add' | 'edit' | 'view';
    caseTitle: string;

    readonly maxWords: number = 150;

    // ----- View mode state (read-only "Read more") -----
    viewContent: string;

    // ----- Add mode state -----
    noteInputs: string[] = [''];
    submitted: boolean = false; // becomes true once Save Notes has been clicked at least once

    // ----- Edit mode state -----
    editContent: string;
    editSubmitted: boolean = false;

    constructor(
        public dialogRef: MatDialogRef<NoteDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: NoteDialogData
    ) {
        this.mode = data.mode;
        this.caseTitle = data.caseTitle || '';
        this.editContent = data.existingContent || '';
        this.viewContent = data.existingContent || '';
    }

    wordCount(text: string): number {
        const trimmed = (text || '').trim();
        return trimmed ? trimmed.split(/\s+/).length : 0;
    }
    private clampToWordLimit(text: string): string {
        const value = text || '';
        const words = value.split(/\s+/).filter(w => w.length > 0);
        if (words.length <= this.maxWords) {
            return value;
        }
        return words.slice(0, this.maxWords).join(' ');
    }

    trackByIndex(index: number): number {
        return index;
    }

    onNoteChange(index: number, value: string): void {
        this.noteInputs[index] = this.clampToWordLimit(value);
    }

    addAnotherNote(): void {
        this.noteInputs.push('');
    }

    removeNote(index: number): void {
        if (this.noteInputs.length <= 1) {
            return; // always keep at least one input visible
        }
        this.noteInputs.splice(index, 1);
    }

    isNoteValid(index: number): boolean {
        return (this.noteInputs[index] || '').trim().length > 0;
    }

    showNoteError(index: number): boolean {
        return this.submitted && !this.isNoteValid(index);
    }

    get allNotesValid(): boolean {
        return this.noteInputs.every((_, i) => this.isNoteValid(i));
    }

    saveNotes(): void {
        this.submitted = true;
        if (!this.allNotesValid) {
            return; // keep dialog open, inline errors now visible, values preserved
        }
        const trimmed = this.noteInputs.map(n => n.trim());
        this.dialogRef.close(trimmed);
    }

    // ---------------------------------------------------------------
    // Edit mode
    // ---------------------------------------------------------------

    onEditChange(value: string): void {
        this.editContent = this.clampToWordLimit(value);
    }

    get editValid(): boolean {
        return (this.editContent || '').trim().length > 0;
    }

    get showEditError(): boolean {
        return this.editSubmitted && !this.editValid;
    }

    saveEdit(): void {
        this.editSubmitted = true;
        if (!this.editValid) {
            return; // keep dialog open, value preserved
        }
        this.dialogRef.close(this.editContent.trim());
    }

    // ---------------------------------------------------------------

    cancel(): void {
        this.dialogRef.close();
    }
}